import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { notificationAPI } from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Buildings, Bell, User, SignOut, Gear, CaretDown, List, X,
  House, Briefcase, FileText, ChartLine, ChatCircle, UsersThree,
  CreditCard, WifiHigh, WifiSlash, Moon, Sun, Package, ArrowsLeftRight, ArrowRight,
} from '@phosphor-icons/react';
import { useTheme } from '../../contexts/ThemeContext';

export const Navbar = () => {
  const { user, logout, activeRole, switchRole, isBoth, isAdmin } = useAuth();
  const { connected: wsConnected, notifications: wsNotifications } = useWebSocket();
  const { isDark, toggleTheme } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      const handleRefresh = () => fetchNotifications();
      window.addEventListener('notifications-updated', handleRefresh);
      return () => {
        clearInterval(interval);
        window.removeEventListener('notifications-updated', handleRefresh);
      };
    }
  }, [user]);

  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications(prev => {
        const newNotifs = [...wsNotifications];
        const existingIds = new Set(newNotifs.map(n => n.notification_id));
        return [...newNotifs, ...prev.filter(n => !existingIds.has(n.notification_id))].slice(0, 10);
      });
      setUnreadCount(prev => Math.max(prev, wsNotifications.filter(n => !n.read).length));
    }
  }, [wsNotifications]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getAll();
      setNotifications(response.data.slice(0, 10));
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {}
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {}
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) await handleMarkRead(notification.notification_id);
    setNotifOpen(false);
    if (notification.link) {
      setTimeout(() => navigate(notification.link), 100);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // For "ambos" users: show ALL links, grouped by role section
  // For single-role users: show only their links
  const clientLinks = [
    { path: '/projects', label: 'Proyectos', icon: Briefcase },
  ];
  const providerLinks = [
    { path: '/quotations', label: 'Cotizaciones', icon: FileText },
    { path: '/proposals', label: 'Propuestas', icon: Package },
  ];
  const sharedLinks = [
    { path: '/price-consultation', label: 'Precios', icon: ChartLine, highlight: true },
    { path: '/analytics', label: 'Analytics', icon: ChartLine },
    { path: '/chat', label: 'Chat', icon: ChatCircle },
  ];
  const adminLinks = [
    { path: '/admin', label: 'Admin', icon: UsersThree },
  ];

  const getVisibleLinks = () => {
    const links = [{ path: '/dashboard', label: 'Dashboard', icon: House }];
    if (isAdmin) {
      links.push(...clientLinks, ...providerLinks, ...sharedLinks, ...adminLinks);
    } else if (isBoth) {
      // Show ALL links for dual-role users
      links.push(...clientLinks, ...providerLinks, ...sharedLinks);
    } else if (user?.role === 'cliente') {
      links.push(...clientLinks, ...sharedLinks);
    } else if (user?.role === 'proveedor') {
      links.push(...providerLinks, ...sharedLinks);
    }
    return links;
  };

  const visibleLinks = getVisibleLinks();

  const getRoleBadge = (link) => {
    if (!isBoth) return null;
    const isClient = clientLinks.some(l => l.path === link.path);
    const isProvider = providerLinks.some(l => l.path === link.path);
    if (isClient) return <span className="ml-1 w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" title="Cliente" />;
    if (isProvider) return <span className="ml-1 w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" title="Proveedor" />;
    return null;
  };

  return (
    <nav className="sticky top-0 z-50 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/matermatch-logo.png" alt="MaterMatch" className="h-9 sm:h-11 w-auto" />
            <div className="hidden sm:block">
              <span className="text-lg font-black text-zinc-600 dark:text-zinc-300 tracking-tight">Mater</span>
              <span className="text-lg font-black text-orange-500 tracking-tight">Match</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-0.5">
            {visibleLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium transition-colors flex items-center ${
                  link.highlight
                    ? isActive(link.path)
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                      : 'text-orange-600 hover:text-orange-800 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/30'
                    : isActive(link.path)
                      ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100'
                      : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <link.icon className="w-4 h-4 mr-1" />
                {link.label}
                {getRoleBadge(link)}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-foreground" data-testid="theme-toggle-btn">
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* Role Switcher for "ambos" users - now visual indicator */}
            {isBoth && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-none border-zinc-300 dark:border-zinc-600 gap-1.5"
                onClick={() => switchRole(activeRole === 'cliente' ? 'proveedor' : 'cliente')}
                data-testid="role-switcher"
              >
                <ArrowsLeftRight className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {activeRole === 'cliente' ? 'Cliente' : 'Proveedor'}
                </span>
                <span className={`w-2 h-2 rounded-full ${activeRole === 'cliente' ? 'bg-emerald-500' : 'bg-orange-500'}`} />
              </Button>
            )}

            {/* Notifications */}
            <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                  <span 
                    className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-zinc-300'}`}
                    title={wsConnected ? 'Notificaciones en tiempo real activas' : 'Reconectando...'}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 rounded-none" data-testid="notifications-dropdown">
                <div className="p-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Notificaciones</h3>
                  {wsConnected ? (
                    <span className="text-xs text-green-600 flex items-center gap-1"><WifiHigh className="w-3 h-3" /> En vivo</span>
                  ) : (
                    <span className="text-xs text-zinc-400 flex items-center gap-1"><WifiSlash className="w-3 h-3" /> Offline</span>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-zinc-500">No hay notificaciones</div>
                  ) : (
                    notifications.map((notif) => (
                      <DropdownMenuItem
                        key={notif.notification_id}
                        onClick={() => handleNotificationClick(notif)}
                        className={`p-3 rounded-none cursor-pointer focus:bg-zinc-50 dark:focus:bg-zinc-800 ${
                          !notif.read ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                        }`}
                        data-testid={`notification-${notif.notification_id}`}
                      >
                        <div className="w-full">
                          <p className="font-medium text-sm text-zinc-900 dark:text-zinc-100">{notif.title}</p>
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{notif.message}</p>
                          {notif.link && (
                            <p className="text-xs text-orange-500 mt-1 flex items-center gap-1">
                              <ArrowRight className="w-3 h-3" /> Ver detalle
                            </p>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </ScrollArea>
                <div className="p-2 border-t border-zinc-200 dark:border-zinc-700">
                  <DropdownMenuItem asChild className="rounded-none justify-center">
                    <Link to="/notifications" className="text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200">
                      Ver todas
                    </Link>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-none" data-testid="user-dropdown">
                <div className="p-3 border-b border-zinc-200 dark:border-zinc-700">
                  <p className="font-semibold text-zinc-900 dark:text-zinc-100">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                  <Badge variant="outline" className="mt-2 rounded-none text-xs">
                    {isBoth ? `${activeRole === 'cliente' ? 'Cliente' : 'Proveedor'} (ambos)` : user?.role}
                  </Badge>
                </div>
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                  <User className="w-4 h-4 mr-2" /> Mi Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/subscriptions')} data-testid="subscriptions-link">
                  <CreditCard className="w-4 h-4 mr-2" /> Suscripciones
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/transactions')} data-testid="transactions-link">
                  <CreditCard className="w-4 h-4 mr-2" /> Historial de Pagos
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                  <Gear className="w-4 h-4 mr-2" /> Configuración
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <SignOut className="w-4 h-4 mr-2" /> Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800 py-2">
            {/* Role switcher for mobile */}
            {isBoth && (
              <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2 font-medium uppercase tracking-wider">Modo activo</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { switchRole('cliente'); }}
                    className={`flex-1 py-2 text-sm font-semibold text-center transition-colors ${
                      activeRole === 'cliente'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                    }`}
                    data-testid="mobile-switch-cliente"
                  >
                    Cliente
                  </button>
                  <button
                    onClick={() => { switchRole('proveedor'); }}
                    className={`flex-1 py-2 text-sm font-semibold text-center transition-colors ${
                      activeRole === 'proveedor'
                        ? 'bg-orange-500 text-white'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                    }`}
                    data-testid="mobile-switch-proveedor"
                  >
                    Proveedor
                  </button>
                </div>
              </div>
            )}

            {/* Mobile: show all links with role indicators */}
            {isBoth && (
              <p className="px-4 pt-3 pb-1 text-xs text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wider">Cliente</p>
            )}
            {(isBoth || user?.role === 'cliente' || isAdmin) && (
              <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${isActive('/dashboard') ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50'}`}>
                <House className="w-5 h-5 inline-block mr-2" />Dashboard
              </Link>
            )}
            {(isBoth || user?.role === 'cliente' || isAdmin) && clientLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${isActive(link.path) ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50'}`}>
                <link.icon className="w-5 h-5 inline-block mr-2" />{link.label}
              </Link>
            ))}

            {isBoth && (
              <p className="px-4 pt-4 pb-1 text-xs text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wider border-t border-zinc-100 dark:border-zinc-800">Proveedor</p>
            )}
            {(isBoth || user?.role === 'proveedor' || isAdmin) && providerLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${isActive(link.path) ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50'}`}>
                <link.icon className="w-5 h-5 inline-block mr-2" />{link.label}
              </Link>
            ))}

            {isBoth && (
              <p className="px-4 pt-4 pb-1 text-xs text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wider border-t border-zinc-100 dark:border-zinc-800">General</p>
            )}
            {sharedLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${
                  link.highlight
                    ? isActive(link.path) ? 'bg-orange-100 text-orange-800' : 'text-orange-600 hover:bg-orange-50'
                    : isActive(link.path) ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50'
                }`}>
                <link.icon className="w-5 h-5 inline-block mr-2" />{link.label}
              </Link>
            ))}

            {isAdmin && adminLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${isActive(link.path) ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50'}`}>
                <link.icon className="w-5 h-5 inline-block mr-2" />{link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};
